// LB-145 option B — the preview side of the marker mechanism.
//
// Armed ONLY when the app is loaded with ?cms=1 by the side-by-side CMS. Everything here is
// inert otherwise: the module is imported for its side effect of defining a function, and that
// function returns immediately unless the flag is present. check-preview-registry.mjs asserts
// that, because a walker running in a real household's session would be a cost on every render
// for no reason, and a data-k attribute in production is a leak of internal keys.
//
// How it works, and why not the obvious thing: t() records key -> RENDERED string as it
// resolves, post-interpolation (src/lib/content.js). This walks the DOM, matches each text
// node against that registry, and sets data-k on the element that holds it. Matching the
// PUBLISHED text instead cannot work for any string with a variable in it — the CMS row holds
// "{count} waiting", the DOM holds "3 waiting".
//
// The click handler deliberately does NOT use capture-phase stopPropagation. LB-146's own
// words: "capture runs on the way down, so every button beneath goes dead at once, silently."
// This listens in the bubble phase and only suppresses the default when the click landed on
// something the editor can select.
import { armPreviewRegistry, previewKeysFor } from "./content.js";

const ATTR = "data-lb-key";

function isCmsPreview() {
  try {
    return new URLSearchParams(window.location.search).get("cms") === "1";
  } catch {
    return false;
  }
}

// Text nodes first (the common case), then element textContent for text split across inline
// elements — a heading with a <b> inside produces no single matching text node.
function markAll(textNodesOnly) {
  const marked = new Set();
  const walker = document.createTreeWalker(document.body, 4 /* NodeFilter.SHOW_TEXT — the constant, not the global: one less thing to be missing */);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const text = (n.nodeValue || "").trim();
    if (!text) continue;
    const keys = previewKeysFor(text);
    if (!keys.length) continue;
    const el = n.parentElement;
    if (!el || marked.has(el)) continue;
    el.setAttribute(ATTR, keys.join(" "));
    marked.add(el);
  }
  // Fallback pass: elements whose whole textContent matches, where no single text node did.
  if (textNodesOnly) return;
  for (const el of document.body.querySelectorAll("h1,h2,h3,h4,p,span,button,label,li,div,a")) {
    if (el.hasAttribute(ATTR)) continue;
    if (el.children.length > 3) continue; // a container, not a string
    const text = (el.textContent || "").trim();
    if (!text || text.length > 400) continue;
    const keys = previewKeysFor(text);
    if (keys.length) el.setAttribute(ATTR, keys.join(" "));
  }
}

// rAF when there is one, a timeout when there is not. Found by LB-149's coverage spec: the
// first version scheduled marking through requestAnimationFrame alone, so in any environment
// without it — jsdom here, and a background tab in a real browser, where rAF is throttled to
// nothing — the registry filled and NOTHING was ever marked. The preview would have looked
// simply dead, with no error to explain it.
let scheduled = false;
const nextFrame = (fn) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(fn) : setTimeout(fn, 16));
function scheduleMark() {
  if (scheduled) return;
  scheduled = true;
  nextFrame(() => {
    scheduled = false;
    try { markAll(); } catch { /* never take the preview down for a marker */ }
  });
}

// Exported so LB-149's coverage spec can drive the REAL marking function rather than a copy of
// its logic. It does nothing unless the registry has been armed, which only happens in preview.
export function markAllForTest() { try { markAll(); } catch { /* the spec asserts the result */ } }
// The text-node pass alone. LB-149 needs it because the textContent fallback also reaches leaf
// elements, so "a leaf is marked" does not prove the text-node pass ran.
export function markTextNodesOnlyForTest() { try { markAll(true); } catch { /* asserted by the spec */ } }
// The real scheduler, so the rAF fallback is exercised rather than bypassed.
export function scheduleMarkForTest() { scheduleMark(); }

export function startCmsPreview() {
  if (!isCmsPreview()) return false;
  armPreviewRegistry();

  const style = document.createElement("style");
  style.textContent =
    `[${ATTR}]{outline:1px dashed rgba(99,102,241,.55);outline-offset:1px;cursor:pointer}` +
    `[${ATTR}]:hover{outline:2px solid #6366f1;background:rgba(99,102,241,.07)}` +
    `[${ATTR}].lb-cms-selected{outline:2px solid #f59e0b;background:rgba(245,158,11,.12)}`;
  document.head.appendChild(style);

  // Re-mark after any render. The observer is cheap because marking is rAF-debounced and
  // skips anything already carrying the attribute.
  new MutationObserver(scheduleMark).observe(document.body, { childList: true, subtree: true, characterData: true });
  scheduleMark();

  document.addEventListener("click", (e) => {
    const el = e.target && e.target.closest ? e.target.closest(`[${ATTR}]`) : null;
    if (!el) return; // a click on something unmarked behaves exactly as the app intends
    e.preventDefault();
    e.stopPropagation();
    document.querySelectorAll(".lb-cms-selected").forEach((x) => x.classList.remove("lb-cms-selected"));
    el.classList.add("lb-cms-selected");
    const keys = (el.getAttribute(ATTR) || "").split(" ").filter(Boolean);
    try {
      window.parent.postMessage({ lbCms: "clicked", keys, text: (el.textContent || "").trim() }, window.location.origin);
    } catch { /* not framed, or a different origin — nothing to report to */ }
  }, false); // bubble, NOT capture — see the note at the top of this file

  // LB-147: the CMS asks for a screen, then for a key to be shown. Navigation is the app's own
  // state change (window.__lbCmsNavigate, registered by App only in preview), then a rAF so the
  // new screen has rendered and been marked before we look for the key.
  window.addEventListener("message", (e) => {
    if (e.origin !== window.location.origin || !e.data || e.data.lbCms !== "goto") return;
    const { route, key } = e.data;
    let navigated = false;
    try { navigated = route ? !!(window.__lbCmsNavigate && window.__lbCmsNavigate(route)) : false; } catch { navigated = false; }
    nextFrame(() => nextFrame(() => {
      try { markAll(); } catch { /* keep going — showing is best-effort */ }
      let found = false;
      if (key) {
        document.querySelectorAll(".lb-cms-selected").forEach((x) => x.classList.remove("lb-cms-selected"));
        for (const el of document.querySelectorAll(`[${ATTR}]`)) {
          if ((el.getAttribute(ATTR) || "").split(" ").includes(key)) {
            el.classList.add("lb-cms-selected");
            el.scrollIntoView({ block: "center", behavior: "smooth" });
            found = true;
            break;
          }
        }
      }
      // Honest about what happened: the CMS says "not on this screen" rather than silently
      // doing nothing, which is the failure a person would blame on the tool.
      try { window.parent.postMessage({ lbCms: "goto-result", key, navigated, found }, window.location.origin); } catch { /* not framed */ }
    }));
  });

  // The CMS sends an override as the editor types; apply it in place so the preview updates
  // without a reload and without a round trip.
  window.addEventListener("message", (e) => {
    if (e.origin !== window.location.origin || !e.data || e.data.lbCms !== "override") return;
    const { key, text } = e.data;
    for (const el of document.querySelectorAll(`[${ATTR}]`)) {
      if ((el.getAttribute(ATTR) || "").split(" ").includes(key)) el.textContent = text;
    }
  });

  return true;
}
